// src/actions/admin_actions.js
import * as AdminAPIUtil from "../util/admin_api_util";

export const RECEIVE_ADMIN_SUMMARY = "RECEIVE_ADMIN_SUMMARY";
export const RECEIVE_PENDING_STUDENTS = "RECEIVE_PENDING_STUDENTS";
export const REMOVE_PENDING_STUDENT = "REMOVE_PENDING_STUDENT";
export const RECEIVE_PENDING_MASTERS = "RECEIVE_PENDING_MASTERS";
export const RECEIVE_ACTIVE_STUDENTS = "RECEIVE_ACTIVE_STUDENTS";
export const RECEIVE_ACTIVE_MASTERS = "RECEIVE_ACTIVE_MASTERS";
export const ADMIN_ERRORS = "ADMIN_ERRORS";

// ===== action creators =====

const receiveAdminSummary = (summary) => ({
  type: RECEIVE_ADMIN_SUMMARY,
  summary,
});

const receivePendingStudents = (students) => ({
  type: RECEIVE_PENDING_STUDENTS,
  students,
});

const removePendingStudent = (studentId) => ({
  type: REMOVE_PENDING_STUDENT,
  studentId,
});

const receivePendingMasters = (masters) => ({
  type: RECEIVE_PENDING_MASTERS,
  masters,
});

const receiveActiveStudents = (students) => ({
  type: RECEIVE_ACTIVE_STUDENTS,
  students,
});

const receiveActiveMasters = (masters) => ({
  type: RECEIVE_ACTIVE_MASTERS,
  masters,
});

const adminErrors = (errors) => ({
  type: ADMIN_ERRORS,
  errors,
});

// ===== thunks =====

// Summary tiles
export const fetchAdminSummary = () => (dispatch) =>
  AdminAPIUtil.fetchAdminSummary()
    .then((res) => dispatch(receiveAdminSummary(res.data)))
    .catch((err) =>
      dispatch(adminErrors(err.response?.data || ["Unable to load summary"]))
    );

// Pending students
export const fetchPendingStudents = () => (dispatch) =>
  AdminAPIUtil.fetchPendingStudents()
    .then((res) => dispatch(receivePendingStudents(res.data)))
    .catch((err) =>
      dispatch(
        adminErrors(err.response?.data || ["Unable to load pending students"])
      )
    );

// Pending masters
export const fetchPendingMasters = () => (dispatch) =>
  AdminAPIUtil.fetchPendingMasters()
    .then((res) => dispatch(receivePendingMasters(res.data)))
    .catch((err) =>
      dispatch(
        adminErrors(err.response?.data || ["Unable to load pending masters"])
      )
    );

// Active (approved) students
export const fetchActiveStudents = () => (dispatch) =>
  AdminAPIUtil.fetchActiveStudents()
    .then((res) => dispatch(receiveActiveStudents(res.data)))
    .catch((err) =>
      dispatch(
        adminErrors(err.response?.data || ["Unable to load active students"])
      )
    );

// Active (approved) masters
export const fetchActiveMasters = () => (dispatch) =>
  AdminAPIUtil.fetchActiveMasters()
    .then((res) => dispatch(receiveActiveMasters(res.data)))
    .catch((err) =>
      dispatch(
        adminErrors(err.response?.data || ["Unable to load active masters"])
      )
    );

// Approve + enroll student
// ✅ scheduleByCourseKey is sent from Dashboard modal
export const approveStudentAndEnroll =
  (studentId, courseKeys, scheduleByCourseKey = {}) =>
  (dispatch) =>
    AdminAPIUtil.approveStudentAndEnroll({
      studentId,
      courseKeys,
      scheduleByCourseKey,
    })
      .then(async (res) => {
        dispatch(removePendingStudent(studentId));

        // IMPORTANT: wait for refreshes so UI updates immediately
        await Promise.all([
          dispatch(fetchAdminSummary()),
          dispatch(fetchActiveStudents()),
          dispatch(fetchPendingStudents()), // optional but helps if list is open
        ]);

        return res;
      })
      .catch((err) =>
        dispatch(
          adminErrors(err.response?.data || ["Unable to approve and enroll student"])
        )
      );


// Delete (reject) pending student
export const deletePendingStudent = (studentId) => (dispatch) =>
  AdminAPIUtil.deletePendingStudent(studentId)
    .then((res) => {
      dispatch(removePendingStudent(studentId));
      dispatch(fetchAdminSummary());
      return res;
    })
    .catch((err) =>
      dispatch(adminErrors(err.response?.data || ["Unable to delete student"]))
    );

// ✅ Convenience wrapper (payload object)
export const approveStudent = (payload) => (dispatch) => {
  const { studentId, courseKeys, scheduleByCourseKey } = payload || {};
  return dispatch(
    approveStudentAndEnroll(studentId, courseKeys, scheduleByCourseKey)
  );
};

export const rejectStudent = (studentId) => deletePendingStudent(studentId);

// =========================
// MASTER APPROVAL
// =========================

export const approveMaster = (masterId) => (dispatch) =>
  AdminAPIUtil.approveMaster(masterId)
    .then((res) => {
      dispatch(fetchPendingMasters());
      dispatch(fetchActiveMasters());
      dispatch(fetchAdminSummary());
      return res;
    })
    .catch((err) => {
      console.error("approveMaster error:", err.response?.data || err);
      dispatch(adminErrors(err.response?.data || ["Unable to approve master"]));
      throw err;
    });

// =========================
// USER COURSE ASSIGNMENT (students + masters)
// =========================

// Assign a user (student or master) to a course
export const assignUserToCourse = (userId, courseKey) => (dispatch) =>
  AdminAPIUtil.assignUserToCourse(userId, courseKey)
    .then((res) => {
      dispatch(fetchActiveStudents());
      dispatch(fetchActiveMasters());
      dispatch(fetchPendingStudents());
      dispatch(fetchPendingMasters());
      return res;
    })
    .catch((err) =>
      dispatch(
        adminErrors(err.response?.data || ["Unable to assign user to course"])
      )
    );

// Remove a user (student or master) from a course
export const removeUserFromCourse = (userId, courseKey) => (dispatch) =>
  AdminAPIUtil.removeUserFromCourse(userId, courseKey)
    .then((res) => {
      dispatch(fetchActiveStudents());
      dispatch(fetchActiveMasters());
      dispatch(fetchPendingStudents());
      dispatch(fetchPendingMasters());
      return res;
    })
    .catch((err) =>
      dispatch(
        adminErrors(err.response?.data || ["Unable to remove user from course"])
      )
    );
